import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { fetchFaqData } from "../../redux/Actions/policyActions";
import { LOCAL_API_BASE_URL } from "../../config";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { postContactForm } from "../../redux/Actions";
import {
  alphabet,
  email,
  phoneNumber,
  alphaNumericPattern,
} from "../../components/core/regex";

const contactUsFormSchema = yup.object({
  name: yup
    .string()
    .required("Name is required")
    .max(50, "Enter cannot exceed 50 character")
    .matches(alphabet, "Name should be only alphabet")
    .trim(),
  email: yup
    .string()
    .required("Email address is required")
    .matches(email, "Enter a valid email address")
    .max(96, "Enter cannot exceed 96 character")
    .trim(),
  mobile: yup
    .string()
    .required("Mobile number is required")
    .matches(phoneNumber, "Enter a valid mobile number")
    .trim(),
  message: yup
    .string()
    .required("Message is required")
    .matches(
      alphaNumericPattern,
      "Message cannot not contain any special character",
    )
    .max(250, "Message cannot exceed 250 character")
    .trim(),
});

function ContactUs() {
  const navigate = useNavigate();
  const [message, setMessage] = useState<string>("");
  const [isError, setIsError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch<any>();
  const settingsData: any = useSelector<any>((state) => state?.settings);
  console.log("settingsData", settingsData);

  useEffect(() => {
    const shopURL = window.location.href;
    const isXcelShop =
      shopURL?.includes("xceldrycleaner") ||
      (window.location.hostname === "localhost" &&
        LOCAL_API_BASE_URL?.includes("xceldrycleaner"));
    const isLaundryUp =
      shopURL?.includes("laundryup") ||
      (window.location.hostname === "localhost" &&
        LOCAL_API_BASE_URL?.includes("laundryup"));

    let metaTitle = "";
    let metaDescription = "";

    if (isXcelShop) {
      metaTitle = "Contact Xcel Dry Cleaners | London E16 1AQ";
      metaDescription =
        "Get in touch with Xcel Dry Cleaners. Call 020 7473 2846, email us, or book a pickup online. Located in Royal Victoria Docks, London.";
    } else if (isLaundryUp) {
      metaTitle = "Contact LaundryUp | Watford Laundry & Dry Cleaning";
      metaDescription =
        "Reach LaundryUp at 103 Eastbury Road, Watford WD19 4JP, or call +44 7578 133757 to book your free laundry & dry cleaning pickup.";
    } else {
      return;
    }

    document.title = metaTitle;
    document.getElementById("title")?.setAttribute("content", metaTitle);
    document.getElementById("description")?.setAttribute("content", metaDescription);
  }, []);

  const {
    handleSubmit: contactFormSubmit,
    control: contactFormControl,
    reset: contactFormReset,
    formState: { errors: contactFormErrors },
  } = useForm({
    resolver: yupResolver(contactUsFormSchema),
    defaultValues: {
      email: "",
      message: "",
      name: "",
      mobile: "",
    },
  });

  const submitContactForm = async (data: any) => {
    setLoading(true);
    try {
      const response: any = await postContactForm(data);
      if (response && response.status) {
        setIsError(false);
        contactFormReset();
        setMessage(response.message);
        setTimeout(() => {
          setMessage("");
        }, 3000);
      } else {
        setIsError(true);
        setMessage(
          response?.message || "Something went wrong. Please try again.",
        );
      }
    } catch (error) {
      setIsError(true);
      setMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="contact-page">
        <div className="breadcrumpset">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="breadcrumpview">
                  <h2>Business Inquiries</h2>
                  <ul>
                    <li>
                      <a href="/">
                        <i className="fa fa-home" aria-hidden="true"></i>{" "}
                      </a>
                    </li>
                    <li>
                      <span>Business Inquiries</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="row">
            <div className="col-md-6">
              <div className="mt-5 mb-4">
                <h2>Business Inquiries</h2>
              </div>
              <form onSubmit={contactFormSubmit(submitContactForm)}>
                <div className="form-contact-page">
                  <div className="contact-form-group mb-3">
                    <label className="form-label">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="name"
                      control={contactFormControl}
                      render={({ field }) => (
                        <input
                          placeholder="Full Name"
                          {...field}
                          className={`form-control ${
                            contactFormErrors?.name ? "invalid" : ""
                          }`}
                        />
                      )}
                    />
                    {contactFormErrors.name ? (
                      <small className="error-msg">
                        {contactFormErrors.name?.message}
                      </small>
                    ) : null}
                  </div>
                  <div className="contact-form-group mb-3">
                    <label className="form-label">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="email"
                      control={contactFormControl}
                      render={({ field }) => (
                        <input
                          placeholder="Email Address"
                          {...field}
                          className={`form-control ${
                            contactFormErrors?.email ? "invalid" : ""
                          }`}
                        />
                      )}
                    />
                    {contactFormErrors.email ? (
                      <small className="error-msg">
                        {contactFormErrors.email?.message}
                      </small>
                    ) : null}
                  </div>
                  <div className="contact-form-group mb-3">
                    <label className="form-label">
                      Phone number <span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="mobile"
                      control={contactFormControl}
                      render={({ field }) => (
                        <input
                          type="text"
                          placeholder="Phone number"
                          {...field}
                          onFocus={(e) => {
                            if (!field.value) {
                              field.onChange("+44 ");
                            }
                          }}
                          className={`form-control ${contactFormErrors?.mobile ? "invalid" : ""}`}
                        />
                      )}
                    />
                    {contactFormErrors.mobile ? (
                      <small className="error-msg">
                        {contactFormErrors.mobile?.message}
                      </small>
                    ) : null}
                  </div>
                  <div className="contact-form-group mb-3">
                    <label className="form-label">
                      Your Message <span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="message"
                      control={contactFormControl}
                      render={({ field }) => (
                        <textarea
                          {...field}
                          placeholder="Your Message"
                          className={`form-control ${
                            contactFormErrors?.message ? "invalid" : ""
                          }`}
                        ></textarea>
                      )}
                    />
                    {contactFormErrors.message ? (
                      <small className="error-msg">
                        {contactFormErrors.message?.message}
                      </small>
                    ) : null}
                  </div>
                  {message != "" ? (
                    <div className="mb-3">
                      <small
                        className={isError ? "text-danger" : "text-success"}
                      >
                        {message}
                      </small>
                    </div>
                  ) : null}
                  <div className="mb-3 text-end">
                    <button
                      type="submit"
                      className="contact-page-btn"
                      disabled={loading}
                    >
                      {loading ? "Sending..." : "Contact us"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <div className="col-md-6">
              <div className="mt-5 mb-4">
                <h2>Google Map</h2>
              </div>
              <div className="">
                <iframe
                  src={
                    (settingsData?.WebsiteSettings?.contact_t1_en &&
                      `https://www.google.com/maps?q=${encodeURIComponent(
                        settingsData.WebsiteSettings.contact_t1_en,
                      )}&output=embed`) ||
                    "https://www.google.com/maps/embed?pb=!1m18..."
                  }
                  width="100%"
                  height="300"
                  loading="lazy"
                />
              </div>
              <div className="contgrid">
                <h2 className="mb-3">GET IN TOUCH</h2>
                <div className="mb-3 d-flex">
                  <span className="me-2">
                    <i className="fa fa-phone"></i>
                  </span>
                  {/* <a>01923510950</a> */}
                  <a href={`tel:${settingsData?.WebsiteSettings?.contact_t3}`}>
                    {settingsData?.WebsiteSettings?.contact_t3}
                  </a>
                </div>
                <div className="mb-3 d-flex">
                  <span className="me-2">
                    <i className="fa fa-envelope-open"></i>
                  </span>
                  <a
                    href={`mailto:${settingsData?.WebsiteSettings?.contact_t6}`}
                  >
                    {settingsData?.WebsiteSettings?.contact_t6}
                    {/* locallaunderette103@gmail.com */}
                  </a>
                </div>
                <div className="mb-0 d-flex">
                  <span className="me-2">
                    <i className="fa fa-map-marker"></i>
                  </span>
                  <p className="mb-0 opacity-75">
                    {settingsData?.WebsiteSettings?.contact_t1_en}
                    {/* 103 Eastbury Road, Watford, WD19 4JP */}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="promo-banner-card">
        <h3 className="promo-banner-title">
          Get 20% OFF On Your First Order Via Mobile Apps.
        </h3>
        <Link
          to="#"
          data-bs-toggle="modal"
          data-bs-target="#quickordermodal"
          className="promo-banner-btn"
        >
          Book Now
        </Link>
      </div>
    </>
  );
}

export default ContactUs;
